import type { SxProps, Theme } from '@mui/material'

/**
 * Responsive sx templates — bám pattern hiện có trong codebase đã chuẩn:
 *   - skill `/convert-form` (form dialog 2-col)
 *   - `GenericSummaryMap.tsx:175` (card grid progressive)
 *   - `LoginPage.tsx:62,96` (flex + grid 2-pane)
 *   - `Layout.tsx:55` (sidebar Drawer responsive)
 *
 * Mục đích: page mới import const sẵn thay vì tự define `gridTemplateColumns`
 * khác nhau → consistent, mobile-first, không lệch breakpoint.
 *
 * Naming convention:
 *   FORM_*   : form dialog grid
 *   CARD_*   : card/tile grid (dashboard, summary, hub)
 *   PANE_*   : 2-pane (sidebar + main)
 *   WIDE_*   : tables/reports nhiều cột (scroll ngang chấp nhận)
 *   TOOLBAR_*: toolbar/filterbar (luôn wrap)
 *   STACK_*  : flex direction responsive (Login pattern)
 */

// ──────────────────────────────────────────────────────────────────────────────
// FORM DIALOG GRIDS — mobile stack 1-col, desktop multi-col
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Form dialog 2-col (Access spec default).
 * Mobile: 1 col stack; sm+: 2 col.
 * Pattern này đã dùng ở 10+ Factory FormDialog + Settings + LocalP base.
 *
 * Usage:
 *   <Box sx={FORM_GRID_2COL}>
 *     <TextField sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 1 } }} />
 *     <TextField sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 1 } }} />
 *   </Box>
 */
export const FORM_GRID_2COL: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 1.5,
  rowGap: 1.25,
  alignItems: 'start',
}

/**
 * Form dialog 3-col (rare — form rất rộng vd LocalP Purchase Contract).
 * Mobile: 1 col; sm: 2 col; md+: 3 col.
 */
export const FORM_GRID_3COL: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(3, minmax(0, 1fr))',
  },
  columnGap: 1.5,
  rowGap: 1.25,
  alignItems: 'start',
}

// ──────────────────────────────────────────────────────────────────────────────
// CARD/TILE GRIDS — dashboard, summary, factory data hub
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Card grid 4-up dashboard (Factory data hub, top-row stats).
 * Mobile: 2 col (compact tiles); md+: 4 col.
 */
export const CARD_GRID_4UP: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
  gap: 1,
}

/**
 * Card grid progressive 2-3-4 (GenericSummaryMap pattern).
 * Mobile: 2 col; sm: 3 col; md+: 4 col. Dùng cho cell grid nhiều ô nhỏ.
 */
export const CARD_GRID_PROGRESSIVE: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(2, 1fr)',
    sm: 'repeat(3, 1fr)',
    md: 'repeat(4, 1fr)',
  },
  gap: 0.5,
}

/**
 * Card grid 3-up (HomePage stats row).
 * Mobile: 1 col stack; md+: 3 col equal.
 */
export const CARD_GRID_3UP: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
  gap: 1.5,
}

// ──────────────────────────────────────────────────────────────────────────────
// 2-PANE LAYOUTS — sidebar + main panel
// ──────────────────────────────────────────────────────────────────────────────

/**
 * 2-pane 8:2 (vd FactoryBaoLocSummaryMap: main bảng kho + side panel info).
 * Mobile stack vertical; md+: 8fr : 2fr ratio.
 */
export const PANE_8_2: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '8fr 2fr' },
  gap: 1.5,
}

/**
 * 2-pane equal (LoginPage hero+form).
 * Mobile stack vertical; md+: 1.12fr : 0.88fr (ưu tiên hero slightly wider).
 */
export const PANE_HERO_FORM: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '1.12fr 0.88fr' },
  gap: { xs: 3, md: 5 },
}

// ──────────────────────────────────────────────────────────────────────────────
// WIDE TABLE/REPORT — chấp nhận scroll ngang trên mobile
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Wrapper cho table/report nhiều cột cứng (vd Futures Margin Report 17 cột,
 * Factory Summary Map sơ đồ kho). Inner content tự đặt minWidth phù hợp.
 *
 * Usage:
 *   <Box sx={WIDE_SCROLL_WRAPPER}>
 *     <Box sx={{ minWidth: 1200, display:'grid', gridTemplateColumns:'...' }}>
 *       ...
 *     </Box>
 *   </Box>
 *
 * KHÔNG dùng `width: 'max(100%, 1460px)'` — buộc viewport scroll cả page.
 * Dùng pattern này để chỉ table scroll ngang, không kéo theo header/sidebar.
 */
export const WIDE_SCROLL_WRAPPER: SxProps<Theme> = {
  width: '100%',
  overflowX: 'auto',
}

// ──────────────────────────────────────────────────────────────────────────────
// TOOLBAR / FILTERBAR — luôn wrap khi overflow
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Toolbar/FilterBar pattern: row trên desktop, column trên mobile.
 * Mỗi child có thể có minWidth riêng để tránh squeeze.
 */
export const TOOLBAR_WRAP: SxProps<Theme> = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  flexWrap: 'wrap',
  gap: 1,
  alignItems: { xs: 'stretch', sm: 'center' },
  rowGap: 1,
}

/**
 * Action button group (Save/Cancel/Delete...) — wrap khi mobile có nhiều nút.
 */
export const ACTION_BUTTONS_WRAP: SxProps<Theme> = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  gap: 0.75,
  '& > button': { minWidth: { xs: 'auto', sm: 80 } },
}

// ──────────────────────────────────────────────────────────────────────────────
// FLEX STACK — vd hero section login
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Flex stack đảo chiều: column mobile, row md+ (LoginPage hero pattern).
 */
export const STACK_COL_TO_ROW: SxProps<Theme> = {
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  alignItems: 'center',
  gap: { xs: 3, md: 5, lg: 7 },
}

// ──────────────────────────────────────────────────────────────────────────────
// COMMON PADDING — page container responsive
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Page container padding — compact mobile, generous desktop.
 * Dùng cho `<Container>`, `<Box>` root của page.
 */
export const PAGE_PADDING: SxProps<Theme> = {
  px: { xs: 1.5, sm: 2, md: 3 },
  py: { xs: 1.5, md: 2 },
}

/**
 * Dialog content padding — match `<DialogContent>` MUI nhưng responsive.
 */
export const DIALOG_CONTENT_PADDING: SxProps<Theme> = {
  px: { xs: 1.5, sm: 2.5 },
  py: { xs: 1.25, sm: 1.75 },
}
